/* eslint-disable no-prototype-builtins */
import dayjs, { OpUnitType, Dayjs } from 'dayjs';
import get from 'lodash/get';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import isUndefined from 'lodash/isUndefined';
import sortBy from 'lodash/sortBy';
import flatten from 'lodash/flatten';
import pickBy from 'lodash/pickBy';
import uniqBy from 'lodash/uniqBy';
import toNumber from 'lodash/toNumber';
import queryString from 'query-string';
import currencyFormatter from 'currency-formatter';
import { CODE_DISCOUNT_TYPE, MANUAL_LABEL_TRANSLATION_KEY } from './constants';

import { getCurrencyFormatter } from '../utils';
import { LineResponse } from './api';
import type { Discount } from './BundleUpdate';
import { isDynamicLine } from '../utils/BundleUtils';

export const defaultLine = {
  ProductId: '',
  RequiresShipping: false,
  LineDiscountedPriceCurrencyCode: '',
  SellingPlanName: '',
  CurrentPriceCurrencyCode: '',
  VariantId: '',
  Quantity: 0,
  NextBillingDate: '',
  CustomerId: '',
  SellingPlanId: '',
  ShopDomain: '',
  NextBillingYearMonth: '',
  SubscriptionContractId: '',
  SubscriptionContractLineId: '',
  VariantImageSrc: '',
  CurrentPriceAmount: '',
  Title: '',
  VariantTitle: '',
  ItemTypeSubscriptionStatusNextBillingDate: '',
  SK: '',
  PK: '',
  LineDiscountedPriceAmount: '',
  SubscriptionId: '',
  FormattedLineTotal: '',
  FormattedItemPrice: '',
  BaBOutOfStockVariants: [],
  PricingPolicyCycleDiscounts: [],
};

export const getQueryParamFromLocation = (
  queryParam: string,
): string | undefined => {
  const search = queryString.parse(window.location.search);
  return search[queryParam] as string | undefined;
};

export const isGid = (id: string): boolean => {
  return id.startsWith('gid://');
};

export const getIdFromGid = (gid = ''): string | undefined => {
  if (gid.indexOf('gid') === -1) {
    return '';
  }
  return gid.split('/').pop();
};

export const getGidFromIdForResource = (
  id: string,
  resource: string,
): string => {
  if (!id || !resource) {
    throw new Error('The method needs an ID and a valid resource');
  }
  return `gid://shopify/${resource}/${id}`;
};

export const validateIsEmail = (email = ''): boolean =>
  !!email && /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z0-9]+$/i.test(email);

export const getNextBillingDate = (subscription: Subscription): Dayjs => {
  const [interval, intervalNumber] =
    subscription.BillingIntervalBillingCount.split('#') as [OpUnitType, string];
  const newDate = dayjs(subscription.NextBillingDate).add(
    parseInt(intervalNumber),
    interval as dayjs.ManipulateType,
  );
  return newDate;
};

export const formatFromProductToGlobalProduct = (
  product: Product,
): GlobalProduct => {
  const formattedProduct: GlobalProduct = {
    id: product.PK,
    title: product.Title,
    description: '',
    price: product.CurrentPriceAmount,
    quantity: product.Quantity,
    priceCurrency: product.CurrentPriceCurrencyCode,
    image: product.VariantImageSrc,
    variants: [],
  };

  return formattedProduct;
};

export const getAllVariants = (
  products: GlobalVariant[] | null,
  variant: Product,
): GlobalVariant[] => {
  return products
    ? products.filter((v) => variant.ProductId == `${v.productId}`)
    : [];
};

export const mergeNewBundlePropsInLine = (
  product: LineItem,
  newBundleProps: {
    productVariantId: string;
    customAttributes: Record<string, string>[];
    BaBOutOfStockVariants: string[];
  },
  pricingPolicy: ComputedPricingPolicy,
  currencyCode: string,
): LineItem => {
  const newProduct = {
    ...product,
    VariantId: newBundleProps.productVariantId || product.VariantId,
    CustomAttributes: newBundleProps.customAttributes,
    BaBOutOfStockVariants: newBundleProps.BaBOutOfStockVariants,
  };

  if (pricingPolicy) {
    const format = getCurrencyFormatter(currencyCode);
    const basePrice = parseFloat(
      pricingPolicy.computedPricingPolicy?.basePrice ||
        pricingPolicy.computedPrice,
    );
    const price = parseFloat(pricingPolicy.computedPrice);
    newProduct.PricingPolicyBasePriceAmount = `${basePrice}`;
    newProduct.LineDiscountedPriceAmount = `${price}`;
    newProduct.FormattedPricingPolicyBasePrice = format(basePrice);
    newProduct.FormattedItemPrice = format(price);
    newProduct.CurrentPriceAmount = `${price}`;
    newProduct.FormattedPricingPolicyBasePriceMultipliedQuantiy = format(
      basePrice * newProduct.Quantity,
    );
    newProduct.FormattedLineTotal = format(price * newProduct.Quantity);
  }
  return newProduct;
};

const calculateTotal = (lines, shipping) =>
  calculateLinesSubtotal(lines) + shipping;

export const calculateLinesSubtotal = (lines: LineItem[]): number =>
  lines?.reduce(
    (acc, { CurrentPriceAmount, Quantity, HideLine, CustomAttributes }) => {
      const isDynamicBaB = CustomAttributes?.find(
        ({ key, value }) => key === '_bundleType' && value === 'dynamicBaB',
      );
      if (isDynamicBaB) {
        if (HideLine) {
          return acc + parseFloat(CurrentPriceAmount) * Quantity;
        }
      } else {
        return acc + parseFloat(CurrentPriceAmount) * Quantity;
      }
      return acc;
    },
    0,
  );

export const updateSubscriptionWithUpdatedLine = (
  subscription: Subscription,
): Subscription => {
  let newSubscription = { ...subscription };
  const format = getCurrencyFormatter(newSubscription.CurrencyCode);

  let SubscriptionTotal = calculateTotal(
    newSubscription.Lines,
    newSubscription.DeliveryPriceAmount,
  );
  const SubscriptionSubTotal = calculateLinesSubtotal(
    newSubscription.Lines as LineItem[],
  );

  const boxDiscountKey = Object.keys(
    newSubscription.SubscriptionLevelDiscount,
  ).find((key) => key.includes('box-discount'));

  let SubscriptionLevelDiscount = {};
  if (boxDiscountKey) {
    const discountPercentageValue =
      newSubscription.SubscriptionLevelDiscount[boxDiscountKey]?.value
        ?.percentage || 0;

    const discountValue =
      SubscriptionSubTotal * (discountPercentageValue / 100);

    SubscriptionTotal = SubscriptionTotal - discountValue;

    SubscriptionLevelDiscount = {
      ...newSubscription.SubscriptionLevelDiscount,
      [boxDiscountKey]: {
        ...newSubscription.SubscriptionLevelDiscount[boxDiscountKey],
        amount: discountValue,
      },
    };
  } else {
    SubscriptionLevelDiscount = {
      ...newSubscription.SubscriptionLevelDiscount,
    };
  }

  newSubscription = {
    ...newSubscription,
    SubscriptionTotal,
    SubscriptionLevelDiscount,
    FormattedSubscriptionTotal: format(SubscriptionTotal),
    SubscriptionSubTotal: SubscriptionSubTotal.toString(),
    FormattedSubscriptionSubtotal: format(SubscriptionSubTotal),
    FormattedDeliveryPrice: format(newSubscription.DeliveryPriceAmount),
  };

  return newSubscription;
};

export const getCustomAttribute = (
  key: string,
  CustomAttributes?: Record<string, string>[],
): string | undefined =>
  CustomAttributes?.find((attr) => attr.key === key)?.value;

export const getUnhiddenCustomAttributes = (
  CustomAttributes: Record<string, string>[],
): Record<string, string>[] =>
  (CustomAttributes || []).filter((attr) => attr.key.slice(0, 1) !== '_');

export const getUnhiddenCustomAttributesString = (
  CustomAttributes: Record<string, string>[],
): string => {
  const unhiddenAttributes = getUnhiddenCustomAttributes(CustomAttributes);
  return unhiddenAttributes
    .reduce((accum, attr) => {
      return accum + `${attr.value} ${attr.key}, `;
    }, '')
    .slice(0, -2);
};

export const transformProductsInBundle = (
  customAttributes: Record<string, string>[] = [],
): Record<string, number> => {
  const productsInBundle = {};
  customAttributes.forEach(({ key, value }) => {
    if (key.startsWith('_pv_')) {
      const productId = key.replace('_pv_', '');
      productsInBundle[productId] = +value;
    }
  });
  return productsInBundle;
};

export const createNewAddonLine = (
  addonLine: GlobalVariant & {
    quantity: number;
    productName: string;
    pricingPolicy?: ComputedPricingPolicy;
  } & {
    line: { id: string };
  },
  isOneTimeAddon?: boolean,
): LineItem => {
  const currency = get(window, ['Shopify', 'currency', 'active'], 'USD');
  const format = getCurrencyFormatter(currency);
  const price = parseFloat(addonLine.price as string);
  const line: LineItem = {
    ...defaultLine,
    SubscriptionContractLineId: addonLine.line?.id,
    ProductId: getGidFromIdForResource(
      addonLine.productId?.toString() || '',
      'Product',
    ),
    VariantId: getGidFromIdForResource(
      addonLine.id.toString() || '',
      'ProductVariant',
    ),
    Title: addonLine.productName,
    VariantTitle: addonLine.title,
    VariantImageSrc: addonLine.image,
    CurrentPriceAmount: addonLine.price.toString(),
    FormattedItemPrice: format(price),
    FormattedLineTotal: format(price * addonLine.quantity),
    LineDiscountedPriceAmount: `${price * addonLine.quantity}`,
    PricingPolicyBasePriceAmount: undefined,
    Quantity: addonLine.quantity,
    RequiresShipping: false,
    OneTimeAddon: !!isOneTimeAddon,
  };

  if (addonLine.pricingPolicy) {
    const pricingPolicy = addonLine.pricingPolicy;
    const basePrice = parseFloat(
      pricingPolicy.computedPricingPolicy?.basePrice ||
        pricingPolicy.computedPrice,
    );
    const price = parseFloat(pricingPolicy.computedPrice);
    line.PricingPolicyBasePriceAmount = `${basePrice}`;
    line.LineDiscountedPriceAmount = `${price * addonLine.quantity}`;
    line.FormattedPricingPolicyBasePrice = format(basePrice);
    line.FormattedItemPrice = format(price);
    line.CurrentPriceAmount = `${price}`;
    line.FormattedPricingPolicyBasePriceMultipliedQuantiy = format(
      basePrice * addonLine.quantity,
    );
    line.FormattedLineTotal = format(price * addonLine.quantity);
  }
  return line;
};

export const updateVariantLine = (
  oldLine: LineItem | undefined = defaultLine,
  variantLine: GlobalVariant & { quantity: number },
  IsSoldOut?: boolean,
  pricingPolicy?: ComputedPricingPolicy,
  currencyCode: string = get(window, ['Shopify', 'currency', 'active'], 'USD'),
): LineItem => {
  const format = getCurrencyFormatter(currencyCode);
  const price = parseFloat(variantLine.price as string);
  const line = {
    ...oldLine,
    VariantId: getGidFromIdForResource(`${variantLine.id}`, 'ProductVariant'),
    Title: variantLine.productName,
    VariantTitle: variantLine.title,
    VariantImageSrc: variantLine.image,
    CurrentPriceAmount: `${variantLine.price}`,
    FormattedItemPrice: format(price),
    FormattedLineTotal: format(price * variantLine.quantity),
    LineDiscountedPriceAmount: `${price * variantLine.quantity}`,
    Quantity: variantLine.quantity,
    IsSoldOut,
  };

  if (pricingPolicy) {
    const basePrice = parseFloat(
      pricingPolicy.computedPricingPolicy?.basePrice ||
        pricingPolicy.computedPrice,
    );
    const price = parseFloat(pricingPolicy.computedPrice);
    line.PricingPolicyBasePriceAmount = `${basePrice}`;
    line.LineDiscountedPriceAmount = `${price * variantLine.quantity}`;
    line.FormattedPricingPolicyBasePrice = format(basePrice);
    line.FormattedItemPrice = format(price);
    line.CurrentPriceAmount = `${price}`;
    line.FormattedPricingPolicyBasePriceMultipliedQuantiy = format(
      basePrice * variantLine.quantity,
    );
    line.FormattedLineTotal = format(price * variantLine.quantity);
  }

  return line;
};

export const mergeGlobalProductInProduct = (
  product: LineItem,
  updateProduct: GlobalVariant,
  quantity: number,
  pricingPolicy: ComputedPricingPolicy,
): LineItem => {
  const format = getCurrencyFormatter(product.LineDiscountedPriceCurrencyCode);
  const price = parseFloat(updateProduct.price as string);
  const newProduct = {
    ...product,
    ProductId: `${updateProduct.productId || updateProduct.id}`,
    VariantId: `${updateProduct.id}`,
    Title: updateProduct.title,
    VariantTitle: updateProduct.variantTitle
      .toLowerCase()
      .includes('default title')
      ? ''
      : updateProduct.variantTitle,
    VariantImageSrc: updateProduct.image || '',
    CurrentPriceAmount: `${updateProduct.price}`,
    FormattedItemPrice: format(price),
    FormattedLineTotal: format(price * quantity),
    LineDiscountedPriceAmount: `${price * quantity}`,
    PricingPolicyBasePriceAmount: `${price}`,
    Quantity: quantity || (updateProduct.quantity as number),
    IsSoldOut: updateProduct.available,
  };

  if (pricingPolicy) {
    const basePrice = parseFloat(
      pricingPolicy.computedPricingPolicy?.basePrice ||
        pricingPolicy.computedPrice,
    );
    const price = parseFloat(pricingPolicy.computedPrice);
    newProduct.PricingPolicyBasePriceAmount = `${basePrice}`;
    newProduct.LineDiscountedPriceAmount = `${price * quantity}`;
    newProduct.FormattedPricingPolicyBasePrice = format(basePrice);
    newProduct.FormattedItemPrice = format(price);
    newProduct.CurrentPriceAmount = `${price}`;
    newProduct.FormattedPricingPolicyBasePriceMultipliedQuantiy = format(
      basePrice * quantity,
    );
    newProduct.FormattedLineTotal = format(price * quantity);
  }

  return newProduct;
};

export const showMinMaxPrice = ({
  min,
  max,
  currency,
}: {
  min?: number | string;
  max?: number | string;
  currency: string;
}): string => {
  if (!min || !max) {
    return '';
  }
  const format = getCurrencyFormatter(currency);
  const maxPrice = format(max as number);
  const minPrice = format(min as number);
  if (maxPrice === minPrice) {
    return `${minPrice}`;
  } else {
    return `${minPrice} - ${maxPrice}`;
  }
};

export const getVariantFromSelectedOptions = (
  currentSelectedOptions,
  variants,
): GlobalVariant => {
  let selectedVariant = {} as GlobalVariant;
  variants.forEach((variant) => {
    let count = 0;
    variant.selectedOptions.forEach((option) => {
      currentSelectedOptions.forEach((currentOption) => {
        if (
          option.name === currentOption.optionName &&
          option.value === currentOption.label
        ) {
          count++;
        }
        if (count === currentSelectedOptions.length) {
          selectedVariant = variant;
        }
      });
    });
  });
  return selectedVariant;
};

export const getVariantFromSelectedOptionsLiquid = (
  currentSelectedOptions,
  variants,
): GlobalVariant => {
  let selectedVariant = {} as GlobalVariant;

  variants.forEach((variant) => {
    const selectedOptions = currentSelectedOptions.map(
      (option) => option.label,
    );
    const areOptionsEquals = isEqual(
      sortBy(variant.options),
      sortBy(selectedOptions),
    );
    if (areOptionsEquals) {
      selectedVariant = variant;
    }
  });
  return selectedVariant;
};

export const getSubscriptionLevelDiscount = (
  lines: LineItem[] | null | undefined,
) => {
  const SubscriptionLevelDiscount = flatten(
    lines
      ?.filter((l) => !l.BaBUUId)
      .map(({ DiscountAllocations }) => {
        return DiscountAllocations || [];
      }),
  ).reduce((memo, discountAllocation) => {
    const { discount, amount } = discountAllocation;

    const { type, title, recurringCycleLimit, usageCount, value } = discount;
    const id =
      type === CODE_DISCOUNT_TYPE ? title : MANUAL_LABEL_TRANSLATION_KEY;

    if (memo[id] == null) {
      memo[id] = {
        amount: 0,
        type,
        label: id,
        recurringCycleLimit,
        usageCount,
        currencyCode: amount.currencyCode,
        value,
      };
    }
    memo[id].amount -= +amount.amount;

    return memo;
  }, {});

  return {
    SubscriptionLevelDiscount,
  };
};

export const getPrepaidCurrentOrdersCount = (
  lines: LineItem[] | null | undefined,
) => {
  const isDynamicBab = lines?.some(isDynamicLine);

  const buildABoxLines = uniqBy(
    lines,
    (line) =>
      line.CustomAttributes?.find(({ key }) => key === '_bundleUid')?.value,
  );

  return (isDynamicBab ? buildABoxLines : lines)
    ?.map((line) => {
      const isPrepaid = line.PrepaidV2 || !!line.PrepaidVersion;
      if (!isPrepaid) {
        return null;
      }

      const totalPrepaidOrders =
        line.PrepaidOrders || line.PrepaidV2Orders || 0;

      const successfulBillingAttempts =
        line?.SuccessfulBillingAttempts?.length || 0;

      const currentOrdersCount = successfulBillingAttempts % totalPrepaidOrders;

      const prepaidDiscount = line.DiscountAllocations?.find(
        ({ discount }) =>
          discount?.value?.percentage === 100 && discount.type === 'MANUAL',
      );
      let amount = 0;
      if (prepaidDiscount) {
        amount = currencyFormatter.format(
          -Math.abs(+prepaidDiscount?.amount?.amount),
          { code: prepaidDiscount?.amount?.currencyCode },
        );
      }
      return {
        currentOrdersCount:
          currentOrdersCount === 0
            ? currentOrdersCount
            : currentOrdersCount + 1,
        totalPrepaidV2Orders: totalPrepaidOrders,
        amount,
      };
    })
    .filter(Boolean);
};

export const hasProductsOutOfStock = (
  lineItems: LineItem[] | null = [],
): boolean => {
  const isOutOfStock = isArray(lineItems)
    ? lineItems.some((lineItem) => {
        return (
          lineItem.SoldOutPolicy === 'DENY' &&
          (lineItem.IsSoldOut ||
            (lineItem?.BaBOutOfStockVariants &&
              lineItem?.BaBOutOfStockVariants?.length > 0))
        );
      })
    : false;
  return isOutOfStock;
};

export const MIN_QUANTITY_KEY = '_minProducts';

export const getMinQuantity = (
  lineItem: LineItem | null,
): number | undefined => {
  if (lineItem?.CustomAttributes) {
    const { value: minQuantity } =
      lineItem.CustomAttributes?.find((i) => i.key === MIN_QUANTITY_KEY) || {};
    if (minQuantity) {
      return parseInt(minQuantity);
    }
  }
  return undefined;
};

const getQuantityFromSubscription = (
  subscription: Subscription,
): number | undefined => {
  const quantity = subscription?.Lines?.reduce((subscriptionQuantity, item) => {
    const minQuantity = getMinQuantity(item);
    const quantity = item.Quantity;

    return minQuantity ? quantity + subscriptionQuantity : subscriptionQuantity;
  }, 0);

  return quantity;
};

export const hasMinQuantityItemsInSubscription = (
  lineItem: LineItem,
  subscription: Subscription,
): boolean => {
  const minQuantity = getMinQuantity(lineItem);

  if (minQuantity) {
    const subscriptionQuantity = getQuantityFromSubscription(subscription);
    return !!subscriptionQuantity && subscriptionQuantity <= minQuantity;
  }

  return false;
};

const getMinQuantityItemsInSubscriptionDinamically = (
  lineItem: LineItem,
  subscription: Subscription,
  quantity: number,
): number | undefined => {
  const dynamicQuantity = subscription?.Lines?.reduce(
    (subscriptionQuantity, item) => {
      let itemQuantity = item.Quantity;
      const minQuantity = getMinQuantity(item);

      if (lineItem.ProductId === item.ProductId) {
        itemQuantity = quantity;
      }

      return minQuantity
        ? itemQuantity + subscriptionQuantity
        : subscriptionQuantity;
    },
    0,
  );

  return dynamicQuantity;
};

export const hasMinQuantityItemsDynamically = (
  lineItem: LineItem,
  subscription: Subscription,
): boolean | ((quantity: number) => boolean) => {
  const minQuantity = getMinQuantity(lineItem);

  if (minQuantity) {
    return (quantity: number) => {
      const subscriptionQuantity = getMinQuantityItemsInSubscriptionDinamically(
        lineItem,
        subscription,
        quantity,
      );

      return !!subscriptionQuantity && subscriptionQuantity <= minQuantity;
    };
  }

  return false;
};

export const getImageProduct = ({
  productId,
  variantId,
  images,
}: {
  productId?: string;
  variantId?: string;
  images: ProductImagesCollection[];
}): string | undefined => {
  if (!productId || !variantId) {
    return '';
  }

  const product = images?.find(
    ({ id }) => id.toString() === productId.toString(),
  );
  const variant = product?.variants?.find(
    ({ id }) => id.toString() === variantId.toString(),
  );

  return variant?.featuredImage || product?.featuredImage;
};

export const getOptimizedImageURL = ({
  url,
  width,
  height,
}: {
  url: string;
  width?: string;
  height?: string;
}): string => {
  const hasProtocol = /^https?/.test(url);
  const baseURL = new URL(`${hasProtocol ? url : `https:${url}`}`);
  const query = new URLSearchParams(baseURL.search);
  const newParams = pickBy({
    width,
    height,
  });

  Object.entries(newParams).forEach(([key, value]) => {
    if (value) {
      query.append(key, value);
    }
  });

  return baseURL.origin + baseURL.pathname + '?' + query.toString();
};

export const isThereEnoughInventory = ({ inventory, quantity }) => {
  const INVENTORY_NOT_MANAGED = 'NOT_MANAGED';
  const INVENTORY_POLICY_CONTINUE = 'CONTINUE';

  // If there is no inventory, we can't check if there is enough
  // so we return true and relay on the backend to check if there is enough
  if (!inventory) {
    return true;
  }

  const {
    inventoryQuantity,
    sellableOnlineQuantity,
    inventoryManagement,
    inventoryPolicy,
  } = inventory;
  const isInventoryTracked =
    inventoryManagement &&
    inventoryManagement.toUpperCase() !== INVENTORY_NOT_MANAGED;
  return isInventoryTracked
    ? (sellableOnlineQuantity ?? inventoryQuantity) > quantity ||
        inventoryPolicy.toUpperCase() === INVENTORY_POLICY_CONTINUE
    : true;
};

export const deleteFromQueryParams = (queryParams: string[]): void => {
  if (window) {
    const url = new URL(window.location.href);

    for (const queryParam of queryParams) {
      url.searchParams.delete(queryParam);
    }

    window.history.replaceState({}, (document.title || '').toString(), url);
  }
};

const applyFixedDiscount = ({ price, quantity, discount }) => {
  const cut = toNumber(discount.DynamicDiscountAmount);
  return (price - cut) * quantity;
};

const applyPercentageDiscount = ({ price, quantity, discount }) => {
  const base = price * quantity;
  const cut = toNumber(discount.DynamicDiscountAmount) / 100;
  return base - base * cut;
};

const applyDiscount = ({ price, quantity, discount }) => {
  if (discount?.DynamicDiscountType === 'percentage') {
    return applyPercentageDiscount({ price, quantity, discount });
  }

  if (discount?.DynamicDiscountType === 'fixed') {
    return applyFixedDiscount({ price, quantity, discount });
  }

  return price * quantity;
};

export const applyDiscountsToLine = ({ price, quantity, discounts }) => {
  return discounts.reduce(
    (acc, discount) => {
      return acc + applyDiscount({ price, quantity, discount });
    },
    discounts.length ? 0 : price * quantity,
  );
};

const findDynamicBaBLine = (lines: LineItem[], lineId: string): LineItem => {
  const line = lines?.find(
    (line: LineItem) =>
      line.SubscriptionContractLineId === lineId && line.BaBUUId,
  );
  return line;
};

export const mergeUpdatedDynamicBaBLines = (
  updatedLines: LineResponse[],
  lines: LineItem[],
  discounts: Discount[],
  lineId: string,
): LineItem[] => {
  const baseAttributesLine = findDynamicBaBLine(lines, lineId);

  if (!baseAttributesLine) {
    return lines;
  }

  const format = getCurrencyFormatter(
    baseAttributesLine.LineDiscountedPriceCurrencyCode,
  );

  lines = lines.filter(({ BaBUUId }) => !BaBUUId);

  const updatedLinesMap: LineItem[] = updatedLines
    .map((line) => {
      if (line.operation === 'remove') {
        lines.splice(
          lines.findIndex(
            (item) => item.SubscriptionContractLineId === line.id,
          ),
          1,
        );
        return {} as LineItem;
      }
      const price = parseFloat(line.updatedPrices.computedPrice as string);
      const linePrice = applyDiscountsToLine({
        price,
        quantity: line.quantity,
        discounts,
      });
      const basePrice = parseFloat(
        line.updatedPrices?.computedPricingPolicy?.basePrice ||
          line.updatedPrices?.computedPrice,
      );

      let baseLine =
        lines.find((l) => l.SubscriptionContractLineId === line.id) ||
        defaultLine;

      baseLine = omit(baseLine, 'BaBUUId');

      return {
        ...baseLine,
        BaBOutOfStockVariants: line.BaBOutOfStockVariants || [],
        PK: baseAttributesLine.PK,
        ProductId: line.productId,
        CustomerId: baseAttributesLine.CustomerId,
        CustomAttributes: baseAttributesLine.CustomAttributes,
        SellingPlanId: baseAttributesLine.SellingPlanId,
        ShopDomain: baseAttributesLine.ShopDomain,
        SubscriptionContractId: baseAttributesLine.SubscriptionContractId,
        SubscriptionContractLineId: line.id,
        VariantId: line.variantId,
        VariantTitle: line.variantTitle,
        Title: line.title,
        Quantity: line.quantity,
        PricingPolicyBasePriceAmount: `${basePrice}`,
        PricingPolicyBasePriceCurrencyCode:
          baseAttributesLine.LineDiscountedPriceCurrencyCode,
        LineDiscountedPriceCurrencyCode:
          baseAttributesLine.LineDiscountedPriceCurrencyCode,
        LineDiscountedPriceAmount: `${linePrice}`,
        FormattedPricingPolicyBasePrice: format(basePrice),
        PricingPolicyCycleDiscounts:
          baseAttributesLine.PricingPolicyCycleDiscounts,
        FormattedItemPrice: format(price),
        CurrentPriceAmount: `${price}`,
        FormattedPricingPolicyBasePriceMultipliedQuantiy: format(
          basePrice * line.quantity,
        ),
        FormattedLineTotal: format(linePrice),
      };
    })
    .filter(({ SubscriptionContractLineId }) => SubscriptionContractLineId);

  return uniqBy([...updatedLinesMap, ...lines], 'SubscriptionContractLineId');
};

/**
 * preventEditFreeItems {boolean} - It's a configure value from the customer portal settings.
 * we want to prevent edit free items by default even if the setting is not set.
 */
export const itemIsFreeProduct = (
  line: LineItem,
  preventEditFreeItems: boolean,
): boolean => {
  if (isUndefined(preventEditFreeItems)) {
    return line.CurrentPriceAmount === '0.0';
  }

  return preventEditFreeItems && line.CurrentPriceAmount === '0.0';
};

export const formatFrequency = (
  t,
  deliveryIntervalDeliveryCount = '',
  fallback = '',
) => {
  if (!t) {
    return fallback;
  }

  if (
    !deliveryIntervalDeliveryCount.length ||
    deliveryIntervalDeliveryCount.indexOf('#') === -1
  ) {
    return fallback;
  }
  const [interval, intervalCount] = deliveryIntervalDeliveryCount.split('#');

  let intervalString = interval.toLowerCase();
  const intervalCountNumber = parseInt(intervalCount, 10);
  let prefix = `${t('frequency.once_a')} `;

  if (intervalCountNumber > 1) {
    prefix = `${t('frequency.every')} ${intervalCountNumber} `;
    intervalString += 's';
  }

  intervalString = t(`frequency.${intervalString}`);

  return prefix + intervalString;
};
